import type { MessagePathNode } from "@/shared/domain/topics";

import type { TopicTreeNode } from "./buildTopicTree";

/**
 * Walk a set of `MessagePathNode` tree-nodes and return the ids of descendant
 * leaves that should be emitted on a tree-node drop given the current filter.
 *
 * A leaf is included if either (1) it is visible (`!hidden`) under the filter,
 * or (2) an ancestor on its path matched the filter by name. Case (2) handles
 * intermediate matches where a parent matches the filter substring but its
 * children don't individually match: the user's intent in dragging the parent
 * is "include everything underneath it."
 *
 * Pass `parentMatched=true` when the caller's own node matched the filter
 * (e.g. a topic whose name matched). That seeds the walk so the entire
 * subtree is included regardless of per-node hidden state.
 */
export function collectVisibleLeafIds(
  children: TopicTreeNode<MessagePathNode>[],
  parentMatched = false,
): Set<string> {
  const ids = new Set<string>();

  function walk(
    node: TopicTreeNode<MessagePathNode>,
    ancestorMatched: boolean,
  ): void {
    if (!ancestorMatched && node.hidden) {
      return;
    }
    if (node.children.length === 0) {
      ids.add(node.data.id);
      return;
    }
    const subtreeIncluded = ancestorMatched || node.match !== undefined;
    for (const child of node.children) {
      walk(child, subtreeIncluded);
    }
  }

  for (const child of children) {
    walk(child, parentMatched);
  }

  return ids;
}
